require('./lineChart.scss');

const PropTypes = require('prop-types');

const React= require('react');
const moment = require('moment');
const d3 = require('d3');
const isEqual = require('lodash/lang/isEqual');
const reduce = require('lodash/collection/reduce');
const { decideAxisDateLabel,
        createTooltip,
        createTopText,
        yAxisFormatLabel
     } = require('./lineChartHelpers.js');

const blackBox = props => {

    const {dataset, width, height} = props;

    return me => {

        const xaxisG = me.select('.line-chart__xaxis');
        const yaxisG = me.select('.line-chart__yaxis');
        const gridG = me.select('.line-chart__grid');
        const lineG = me.select('.line-chart__line');
        const areaG = me.select('.line-chart__area');
        const tooltip = me.select('.line-chart__tooltip');
        const topText = me.select('.line-chart__top-text');
        const data = dataset.toJS();
        const dataArray = data.length > 30 ? data.slice(-30) : data;
        const maxDate = d3.max(dataArray, d => moment.utc(d.historicalDate));
        const minDate = d3.min(dataArray, d => moment.utc(d.historicalDate));
        const maxVal = d3.max(dataArray, d => +d.total);
        const minVal = d3.min(dataArray, d => +d.total);
        const xDomain = [ minDate, maxDate ];
        const yDomain = [ minVal, maxVal ];
        const xAxisFormat = decideAxisDateLabel(maxDate, minDate);
        const yAxisFormat = (d) => yAxisFormatLabel(d);

        const padding = 40;

        const leftPadding = maxVal > 999 ? 60 : 40;

        var bisectDate = d3.bisector(function(d) { return d.historicalDate; }).left;
    
        const xValues = dataArray.filter((d, i) => !!(i % 2))
            .map(d => new Date(d.historicalDate));

        const yScale = d3.scale.linear()
            .domain(yDomain)
            .range([height - padding, padding]);

        const xScale = d3.time.scale()
            .domain(xDomain)
            .range([leftPadding * 2, width]);

        const yAxis = d3.svg.axis()
            .orient('left')
            .scale(yScale)
            .innerTickSize(-width)
            .outerTickSize(0)
            .tickPadding(1)
            .tickFormat(yAxisFormat);
            
            const xAxis = d3.svg.axis()
            .orient('bottom')
            .scale(xScale)
            .innerTickSize(-height+(padding*2))
            .outerTickSize(0)
            .tickPadding(1)
            .tickFormat(xAxisFormat)
            .tickValues(xValues);

        const lineArray = dataArray.map(item => [xScale(moment.utc(item.historicalDate)), yScale(item.total)] );

        const lineDataArrayMap = dataArray.map(item => {
            return {historicalDate: new Date(item.historicalDate), total: item.total}
        });

        const drawLine = d3.svg.line()
                .interpolate('linear');

        const prepareTransition = d3.svg.line()
                .y(() => { return height - padding; })
                .interpolate('linear');

        const x = dateString => xScale(moment.utc(dateString));

        const y = amount => yScale(amount);

        const drawArea = d3.svg.area()
                .y0(height - padding)
                .interpolate('linear');

        gridG.selectAll('line').remove();
        areaG.selectAll('path').remove();
        lineG.selectAll('path').remove();

        let line = lineG.append('path')
                .datum(lineArray);

        let area = areaG.append('path')
                .datum(lineArray);

        line.attr('class', 'line')
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawLine);

        let focus = areaG.append("g")
            .attr("class", ".line-chart__focus")
            .style("display", "none");


        focus.append("circle")
            .attr("r", 5);

        function mousemove() {
            let x0 = xScale.invert(d3.mouse(this)[0]),
                i = bisectDate(lineDataArrayMap, x0, 1),
                d0 = lineDataArrayMap[i - 1],
                d1 = lineDataArrayMap[i],
                d = x0 - d0.historicalDate > d1.historicalDate - x0 ? d1 : d0;
            focus.attr("transform", "translate(" + x(d.historicalDate) + "," + y(d.total) + ")");
            tooltip.html(createTooltip(d))
                .style('left', () => `${x(d.historicalDate)}px`)
                .style('top', () => `${(y(d.total))}px`)
                .style('display', 'block');
        }

        yaxisG.attr('class', 'line-chart__yaxis line-chart__axis')
            .attr('transform', 'translate('+leftPadding*2+',0)')
            .call(yAxis);

        xaxisG.attr('class', 'line-chart__xaxis line-chart__axis')
        .attr('transform', 'translate(0,' + (height-padding) + ')')
            .call(xAxis)
            .selectAll('.line-chart__xaxis .tick text')
                .call(function(tick){
                    let spacing = 0;
                    tick.each(function(t, index) {
                        let self = d3.select(this);
                        let s = self.text().split(' ');
                        self.text('');
                        spacing = index === (xValues.length - 1) ? -10 : index === 0 && spacing === -5 ? 5 : 0
                        self.append("tspan")
                            .attr("x", spacing)
                            .attr("dy",".8em")
                            .text(s[0]);
                        self.append("tspan")
                            .attr("x", spacing)
                            .attr("dy","12")
                            .text(s[1]);
                    });
               });

        xaxisG.selectAll('.line-chart__xaxis text')
            .attr('transform', 'translate(0, 10)');

        yaxisG.selectAll('.line-chart__yaxis text')
            .attr('transform', `translate(-${leftPadding-32}, 0)`);

        area.attr('class', 'area')
            .on("mouseover", function() { focus.style("display", null); })
            .on("mouseout", function() {
                focus.style("display", "none");
                tooltip.style('display', 'none');
            })
            .on("mousemove", mousemove)
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawArea);

        const today = dataArray[dataArray.length - 1];
        topText.html(createTopText(today));
    }
}

class LineChart extends React.Component {

    constructor(props) {
        super(props);
        this.setNode = this.setNode.bind(this);
    }

    componentDidMount() {
        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    componentDidUpdate(old) {
        let difference = reduce(this.props, (result, value, key) => {
            return isEqual(value, old[key]) ? result : result.concat(key);
        }, []);

        if (difference.length === 0) { return; }

        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    setNode(ref) {
        this.nodeReference = ref;
    }

    render() {
        return (
            <div ref={this.setNode} className="line-chart">

                <svg width={this.props.width}
                    height={this.props.height}
                    shapeRendering="geometricPrecision"
                >
                    <defs>
                        <linearGradient id="linear" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop className="line-chart__stop1" offset="0%"/>
                            <stop className="line-chart__stop2" offset="100%"/>
                        </linearGradient>
                    </defs>

                    <g className="line-chart__grid"></g>
                    <g className="line-chart__xaxis"></g>
                    <g className="line-chart__yaxis"></g>
                    <g className="line-chart__line">
                        <path></path>
                    </g>
                    <g className="line-chart__area">
                        <path></path>
                    </g>
                    <g className="line-chart__circles"></g>
                </svg>
                <div className="line-chart__tooltip"></div>
                <div className="line-chart__top-text"></div>

            </div>
        );
    }
}

LineChart.propTypes = {
    dataset: PropTypes.object.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired
};

module.exports = LineChart;
